import React, { useEffect, useRef } from 'react';
import CheckIcon from '@mui/icons-material/Check';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Box, IconButton, Typography } from '@mui/material';

const pixelRoundedShape = 'polygon(8px 0, calc(100% - 8px) 0, calc(100% - 8px) 4px, calc(100% - 4px) 4px, calc(100% - 4px) 8px, 100% 8px, 100% calc(100% - 8px), calc(100% - 4px) calc(100% - 8px), calc(100% - 4px) calc(100% - 4px), calc(100% - 8px) calc(100% - 4px), calc(100% - 8px) 100%, 8px 100%, 8px calc(100% - 4px), 4px calc(100% - 4px), 4px calc(100% - 8px), 0 calc(100% - 8px), 0 8px, 4px 8px, 4px 4px, 8px 4px)';

const getTaskLabel = (task) => {
  if (Array.isArray(task?.full_title) && task.full_title.length > 0) {
    return String(task.full_title[task.full_title.length - 1]);
  }
  return task?.title || 'Untitled task';
};

const TaskCheckmark = ({ isCurrent = false }) => (
  <Box
    aria-hidden="true"
    data-testid="task-complete-indicator"
    sx={{
      position: 'absolute',
      top: isCurrent ? { xs: -3, sm: -6 } : { xs: -7, sm: -9 },
      right: isCurrent ? { xs: -4, sm: -7 } : { xs: -5, sm: -7 },
      width: { xs: 22, sm: 28 },
      height: { xs: 22, sm: 28 },
      display: 'grid',
      placeItems: 'center',
      borderRadius: 0,
      clipPath: pixelRoundedShape,
      color: 'background.default',
      bgcolor: '#090D0E',
      filter: 'drop-shadow(0 0 8px rgba(57,255,20,0.8))',
      zIndex: 10,
      pointerEvents: 'none',
      '&::before': {
        content: '""',
        position: 'absolute',
        inset: { xs: 2, sm: 3 },
        bgcolor: 'green.main',
        clipPath: pixelRoundedShape,
        zIndex: 0,
      },
      '& .MuiSvgIcon-root': { position: 'relative', zIndex: 1 },
    }}
  >
    <CheckIcon sx={{ fontSize: { xs: 14, sm: 18 } }} />
  </Box>
);

const SideTask = ({ task, position, onSelect }) => {
  if (!task) return <Box aria-hidden="true" sx={{ minWidth: 0 }} />;
  const isComplete = Boolean(task.complete);
  const isPrevious = position === 'previous';
  const borderColor = isComplete ? '#39FF14' : isPrevious ? '#FF2D95' : '#00E5FF';
  const fillBackground = isComplete
    ? 'linear-gradient(180deg, #39FF14 0%, #1D830D 100%)'
    : isPrevious
      ? 'linear-gradient(180deg, #121614 0%, #42162D 100%)'
      : 'linear-gradient(180deg, #121614 0%, #182D30 100%)';

  return (
    <Box
      component="button"
      type="button"
      aria-label={`${getTaskLabel(task)}, ${isComplete ? 'complete' : 'not complete'}`}
      onClick={onSelect}
      sx={{
        position: 'relative',
        width: '100%',
        minWidth: 0,
        maxWidth: 145,
        justifySelf: position === 'previous' ? 'end' : 'start',
        px: { xs: 0.5, sm: 1.25, md: 1.5 },
        py: { xs: 0.75, sm: 0.9 },
        m: 0,
        border: 0,
        borderRadius: 0,
        clipPath: pixelRoundedShape,
        background: borderColor,
        color: isComplete ? 'background.default' : isPrevious ? 'secondary.main' : 'primary.main',
        filter: isComplete
          ? 'drop-shadow(0 5px 7px rgba(57,255,20,0.42))'
          : isPrevious
            ? 'drop-shadow(0 5px 7px rgba(255,45,149,0.3))'
            : 'drop-shadow(0 5px 6px rgba(0,229,255,0.2))',
        cursor: 'pointer',
        transition: 'transform 150ms ease, border-color 150ms ease, filter 150ms ease',
        zIndex: 2,
        isolation: 'isolate',
        '&::before': {
          content: '""',
          position: 'absolute',
          inset: 2,
          background: fillBackground,
          clipPath: pixelRoundedShape,
          zIndex: -1,
        },
        '&:hover': {
          transform: 'translateY(-4px)',
          background: isComplete ? '#39FF14' : '#00E5FF',
          filter: isComplete
            ? 'drop-shadow(0 8px 9px rgba(57,255,20,0.55))'
            : 'drop-shadow(0 8px 9px rgba(0,229,255,0.38))',
        },
        '&:active': { transform: 'translateY(2px)' },
        '&:focus-visible': { outline: '3px solid #FFFFFF', outlineOffset: 3 },
      }}
    >
      <Typography
        component="span"
        variant="caption"
        sx={{
          display: 'block',
          width: '100%',
          color: 'inherit',
          fontWeight: 700,
          fontSize: { xs: '0.46rem', sm: '0.65rem' },
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {getTaskLabel(task)}
      </Typography>
    </Box>
  );
};

const CurrentTask = ({ task }) => {
  const isComplete = Boolean(task.complete);
  const borderColor = isComplete ? '#39FF14' : '#FF5F1F';
  const fillBackground = isComplete
    ? 'linear-gradient(180deg, #39FF14 0%, #1D830D 100%)'
    : 'linear-gradient(180deg, #121614 0%, #4A2114 100%)';

  return (
    <Box
      key={task.id}
      role="status"
      aria-label={`${getTaskLabel(task)}, current task, ${isComplete ? 'complete' : 'not complete'}`}
      sx={{
        position: 'relative',
        width: '100%',
        minWidth: 0,
        maxWidth: 280,
        justifySelf: 'center',
        px: { xs: 1, sm: 2.25, md: 2.75 },
        py: { xs: 1.4, sm: 1.7, md: 2 },
        border: 0,
        borderRadius: 0,
        clipPath: pixelRoundedShape,
        background: borderColor,
        filter: isComplete
          ? 'drop-shadow(0 8px 10px rgba(57,255,20,0.48))'
          : 'drop-shadow(0 8px 10px rgba(255,95,31,0.45))',
        overflow: 'visible',
        zIndex: 4,
        isolation: 'isolate',
        animation: 'taskFloat 2s ease-in-out infinite, taskArrive 220ms ease-out',
        '@keyframes taskFloat': {
          '0%, 100%': { transform: 'translateY(-3px)' },
          '50%': { transform: 'translateY(-9px)' },
        },
        '@keyframes taskArrive': {
          from: { opacity: 0, scale: '0.94' },
          to: { opacity: 1, scale: '1' },
        },
        '&::before': {
          content: '""',
          position: 'absolute',
          inset: 3,
          background: fillBackground,
          clipPath: pixelRoundedShape,
          zIndex: -1,
          pointerEvents: 'none',
        },
      }}
    >
      <Typography
        variant="body1"
        sx={{
          position: 'relative',
          zIndex: 1,
          color: isComplete ? 'background.default' : 'primary.main',
          fontWeight: 800,
          fontSize: { xs: '0.62rem', sm: '0.78rem', md: '0.82rem' },
          textAlign: 'center',
          textTransform: 'uppercase',
          overflow: 'hidden',
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflowWrap: 'anywhere',
          textShadow: isComplete ? 'none' : '0 2px 0 #001F24, 0 0 8px rgba(0,229,255,0.35)',
        }}
      >
        {getTaskLabel(task)}
      </Typography>
    </Box>
  );
};

const TaskNavigationBar = ({ tasks, currentTaskIndex, onSelectTask, containScroll = false }) => {
  const taskRefs = useRef([]);
  const currentTask = tasks[currentTaskIndex];
  const previousTask = tasks[currentTaskIndex - 1];
  const nextTask = tasks[currentTaskIndex + 1];
  const progress = tasks.length > 1 ? (currentTaskIndex / (tasks.length - 1)) * 100 : 100;

  useEffect(() => {
    const activeTask = taskRefs.current[currentTaskIndex];
    if (containScroll && activeTask) {
      const viewport = activeTask.parentElement.parentElement;
      viewport.scrollLeft = activeTask.offsetLeft - (viewport.clientWidth - activeTask.offsetWidth) / 2;
      return;
    }
    if (typeof activeTask?.scrollIntoView === 'function') {
      activeTask.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    }
  }, [containScroll, currentTaskIndex]);

  const selectPrevious = () => {
    if (currentTaskIndex > 0) onSelectTask(currentTaskIndex - 1);
  };

  const selectNext = () => {
    if (currentTaskIndex < tasks.length - 1) onSelectTask(currentTaskIndex + 1);
  };

  if (!currentTask) return null;

  return (
    <Box
      id="task-navigation-slider"
      component="nav"
      aria-label="Task navigation"
      sx={{
        position: 'relative',
        width: '100%',
        maxWidth: { sm: 620, md: 680 },
        minHeight: { xs: 108, sm: 132, md: 145 },
        mt: 1.5,
        mx: 'auto',
        display: 'flex',
        alignItems: 'center',
        userSelect: 'none',
      }}
    >
      <Box
        sx={{
          position: 'relative',
          zIndex: 1,
          width: '100%',
          display: 'grid',
          gridTemplateColumns: { xs: '30px minmax(0, 1fr) 30px', sm: '38px minmax(0, 1fr) 38px', md: '40px minmax(0, 1fr) 40px' },
          alignItems: 'center',
          gap: { xs: 0.25, sm: 0.75, md: 1 },
        }}
      >
        <IconButton
          aria-label="Previous task"
          onClick={selectPrevious}
          disabled={!previousTask}
          sx={navigationButtonStyles}
        >
          <ChevronLeftIcon />
        </IconButton>

        <Box
          data-testid="task-scroll"
          sx={{
            width: '100%',
            minWidth: 0,
            overflowX: 'auto',
            overflowY: 'hidden',
            py: { xs: 1.5, sm: 2 },
            WebkitOverflowScrolling: 'touch',
            touchAction: 'pan-x',
            scrollbarWidth: 'none',
            '&::-webkit-scrollbar': { display: 'none' },
          }}
        >
          <Box
            sx={{
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              gap: { xs: 0.75, sm: 1.25 },
              width: 'max-content',
              minWidth: '100%',
              px: { xs: 'calc(50% - 56px)', sm: 'calc(50% - 100px)' },
            }}
          >
            <Box
              aria-hidden="true"
              sx={{
                position: 'absolute',
                left: { xs: 'calc(50% - 56px)', sm: 'calc(50% - 100px)' },
                right: { xs: 'calc(50% - 56px)', sm: 'calc(50% - 100px)' },
                top: '50%',
                height: 8,
                transform: 'translateY(-50%)',
                bgcolor: '#111614',
                borderRadius: 999,
                boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.8)',
                zIndex: 0,
              }}
            >
              <Box
                sx={{
                  width: `${progress}%`,
                  height: '100%',
                  borderRadius: 999,
                  background: 'linear-gradient(90deg, #FF2D95 0%, #FF5F1F 70%, #FF5F1F 100%)',
                  boxShadow: '0 0 20px rgba(255,95,31,0.9)',
                  transition: 'width 420ms ease',
                }}
              />
            </Box>

            {tasks.map((task, index) => {
              const isCurrent = index === currentTaskIndex;
              return (
                <Box
                  key={task.id || index}
                  ref={(element) => { taskRefs.current[index] = element; }}
                  sx={{
                    position: 'relative',
                    zIndex: isCurrent ? 4 : 2,
                    flex: isCurrent
                      ? { xs: '0 0 112px', sm: '0 0 200px' }
                      : { xs: '0 0 70px', sm: '0 0 125px' },
                  }}
                >
                  {Boolean(task.complete) && <TaskCheckmark isCurrent={isCurrent} />}
                  {isCurrent ? (
                    <CurrentTask task={task} />
                  ) : (
                    <SideTask
                      task={task}
                      position={index < currentTaskIndex ? 'previous' : 'next'}
                      onSelect={() => onSelectTask(index)}
                    />
                  )}
                </Box>
              );
            })}
          </Box>
        </Box>

        <IconButton
          aria-label="Next task"
          onClick={selectNext}
          disabled={!nextTask}
          sx={navigationButtonStyles}
        >
          <ChevronRightIcon />
        </IconButton>
      </Box>
    </Box>
  );
};

const navigationButtonStyles = {
  width: { xs: 30, sm: 38, md: 40 },
  height: { xs: 30, sm: 38, md: 40 },
  p: 0,
  m: 0,
  color: 'green.main',
  bgcolor: '#121614',
  border: 0,
  borderRadius: 0,
  clipPath: pixelRoundedShape,
  background: '#59605D',
  filter: 'drop-shadow(0 4px 0 #060807)',
  zIndex: 5,
  isolation: 'isolate',
  transition: 'transform 150ms ease, border-color 150ms ease, filter 150ms ease',
  '& .MuiSvgIcon-root': { fontSize: { xs: 20, sm: 24 } },
  '&::before': {
    content: '""',
    position: 'absolute',
    inset: 2,
    bgcolor: '#121614',
    clipPath: pixelRoundedShape,
    zIndex: -1,
  },
  '&:hover': {
    color: 'green.main',
    background: '#39FF14',
    filter: 'drop-shadow(0 5px 0 #176b0a) drop-shadow(0 0 8px rgba(57,255,20,0.55))',
    transform: 'translateY(-2px)',
  },
  '&:active': { transform: 'translateY(3px)', filter: 'drop-shadow(0 1px 0 #060807)' },
  '&.Mui-disabled': { color: '#59605D', borderColor: '#333936', opacity: 0.3 },
};

export default TaskNavigationBar;
